// src/types/notification.types.ts
// ============================================================
// SALT — Notification Type Definitions
// Mirrors backend API response shapes from notification_routes.ts
// ============================================================

// ── Notification Type Enum ──────────────────────────────────

export type NotificationType =
  | 'team_invite'
  | 'invite_accepted'
  | 'invite_declined'
  | 'match_accepted'
  | 'match_declined'
  | 'match_expired'
  | 'match_ready'
  | 'game_result_reported'
  | 'match_completed'
  | 'match_disputed'
  | 'dispute_resolved'
  | 'member_joined'
  | 'member_left'
  | 'captain_transferred'
  | 'kicked_from_team'
  | 'rank_milestone'
  | 'season_ending'
  | 'admin_announcement';

// ── Related Entity Types ────────────────────────────────────

export type NotificationRelatedType = 'team' | 'match' | 'dispute' | 'season' | null;

// ── Single Notification (from GET /api/notifications) ───────

export interface Notification {
  notificationId: number;
  type: NotificationType;
  title: string;
  message: string;
  relatedType: NotificationRelatedType;
  relatedId: number | null;
  actionUrl: string | null;
  actionLabel: string | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
  expiresAt: string | null;
}

// ── Unread Notification (from GET /api/notifications/unread) ─
// Slightly slimmer — no isRead/readAt/expiresAt fields

export interface UnreadNotification {
  notificationId: number;
  type: NotificationType;
  title: string;
  message: string;
  relatedType: NotificationRelatedType;
  relatedId: number | null;
  actionUrl: string | null;
  actionLabel: string | null;
  createdAt: string;
}

// ── API Response Shapes ─────────────────────────────────────

export interface NotificationsResponse {
  notifications: Notification[];
  total: number;
  limit: number;
  offset: number;
}

export interface UnreadNotificationsResponse {
  notifications: UnreadNotification[];
  count: number;
}

export interface UnreadCountResponse {
  unreadCount: number;
}

export interface NotificationActionResponse {
  success: boolean;
}

// ── Notification Category (for icon mapping) ────────────────

export type NotificationCategory = 'team' | 'match' | 'dispute' | 'rank' | 'season' | 'admin';

/**
 * Maps a notification type to a visual category for icon selection.
 */
export function getNotificationCategory(type: NotificationType): NotificationCategory {
  switch (type) {
    case 'team_invite':
    case 'invite_accepted':
    case 'invite_declined':
    case 'member_joined':
    case 'member_left':
    case 'captain_transferred':
    case 'kicked_from_team':
      return 'team';

    case 'match_accepted':
    case 'match_declined':
    case 'match_expired':
    case 'match_ready':
    case 'game_result_reported':
    case 'match_completed':
      return 'match';

    case 'match_disputed':
    case 'dispute_resolved':
      return 'dispute';

    case 'rank_milestone':
      return 'rank';

    case 'season_ending':
      return 'season';

    case 'admin_announcement':
      return 'admin';

    default:
      return 'admin';
  }
}